// ── Habit units ──────────────────────────────────────────────────────────────
// A To-Do habit's daily goal is a number (`dailyCount`) and the day's log is a
// number too (habitLogs[date][habitId]); the day is done once the log reaches
// the goal. A unit only says what that number counts:
//   count   — "times a day" (the original meaning; 1-10, one tap = +1)
//   steps / pages / glasses / custom — an amount ("3/8 glasses")
//   time    — minutes ("20 min / 1h")
// So every done / streak / score / widget check keeps working unchanged, and a
// unit change rides the same goal history as a times-per-day change (`unit`
// and `unitLabel` are goal fields next to `dailyCount`, see habitWeeklyGoal.js).
// Habits saved before this have no `unit` and read as "count".
// Only To-Do habits have a unit; full-stop / limited habits ignore it.

export const HABIT_UNITS = [
  { id: "count", label: "Count", one: "time", many: "times" },
  { id: "steps", label: "Steps", one: "step", many: "steps", goal: 10000, max: 100000, quick: [500, 1000, 2000] },
  { id: "pages", label: "Pages", one: "page", many: "pages", goal: 10, max: 2000, quick: [1, 5, 10] },
  { id: "glasses", label: "Glasses", one: "glass", many: "glasses", goal: 8, max: 50, quick: [1, 2] },
  { id: "time", label: "Time", one: "min", many: "min", goal: 30, max: 1440, quick: [5, 15, 30] },
  { id: "custom", label: "Custom unit", one: "", many: "", goal: 1, max: 99999, quick: [1, 5], decimals: true },
];

// Built-in choices under "Custom unit", grouped for the habit form's unit
// picker (a pop-up with one heading per group, plus "Other" to type any name).
// Each is stored as `unitLabel` exactly like a typed name, so a habit is just
// { unit: "custom", unitLabel: "km" } either way — a preset only adds a
// starting goal, quick-add amounts, a singular form and (money) a prefix.
// Nothing here repeats a top-level unit: no steps/pages/glasses, and no time
// group (the Time unit covers minutes, hours and sleep).
const u = (label, goal, quick, one) => ({ label, goal, quick, ...(one ? { one } : {}) });
const UNIT_GROUPS = [
  { title: "Distance", units: [u("km", 5, [1, 2, 5]), u("mi", 3, [0.5, 1, 2]), u("meters", 1000, [100, 250, 500], "meter")] },
  { title: "Volume", units: [u("ml", 2000, [250, 500]), u("L", 2, [0.5, 1]), u("oz", 64, [8, 16]), u("cups", 8, [1, 2], "cup"), u("bottles", 2, [1], "bottle")] },
  { title: "Weight / food", units: [u("g", 100, [10, 50, 100]), u("kg", 1, [0.5, 1]), u("kcal", 500, [100, 250, 500]), u("lb", 2, [0.5, 1])] },
  { title: "Money", money: true, units: [] },
  { title: "Fitness", units: [u("reps", 50, [10, 20, 50], "rep"), u("sets", 3, [1], "set"), u("laps", 10, [1, 5], "lap"), u("floors", 10, [1, 5], "floor"), u("rounds", 3, [1], "round")] },
  { title: "Mind & study", units: [u("chapters", 1, [1], "chapter"), u("words", 500, [100, 250, 500], "word"), u("sessions", 1, [1], "session"), u("lessons", 1, [1], "lesson")] },
  { title: "Life", units: [u("meals", 3, [1], "meal"), u("servings", 5, [1], "serving"), u("pills", 1, [1], "pill"), u("vitamins", 1, [1], "vitamin"), u("doses", 1, [1], "dose")] },
];

// Money follows the phone's region (Jade, 2026-10-09): the Money group shows
// one currency, the local one. Every symbol below is still recognised on any
// phone, so a "₱" habit reads "₱40/₱100" wherever it's opened. Keyed by
// symbol, since that's what's stored; countries sharing a symbol share a row.
const CURRENCIES = [
  { label: "₱", goal: 100, quick: [20, 50, 100], regions: ["PH"] },
  { label: "$", goal: 5, quick: [1, 5, 10], regions: ["US", "CA", "AU", "NZ", "SG", "HK", "MX", "TW"] },
  { label: "€", goal: 5, quick: [1, 5, 10], regions: ["DE", "FR", "ES", "IT", "NL", "BE", "AT", "PT", "IE", "FI", "GR", "SK", "SI", "LT", "LV", "EE", "LU", "MT", "CY", "HR"] },
  { label: "£", goal: 5, quick: [1, 5, 10], regions: ["GB"] },
  { label: "¥", goal: 500, quick: [100, 500, 1000], regions: ["JP", "CN"] },
  { label: "₩", goal: 5000, quick: [1000, 5000, 10000], regions: ["KR"] },
  { label: "₹", goal: 100, quick: [50, 100, 500], regions: ["IN"] },
  { label: "Rp", goal: 20000, quick: [5000, 10000, 20000], regions: ["ID"] },
  { label: "₫", goal: 50000, quick: [10000, 20000, 50000], regions: ["VN"] },
  { label: "฿", goal: 100, quick: [20, 50, 100], regions: ["TH"] },
  { label: "RM", goal: 10, quick: [5, 10, 20], regions: ["MY"] },
  { label: "R$", goal: 10, quick: [5, 10, 20], regions: ["BR"] },
  { label: "₦", goal: 1000, quick: [500, 1000], regions: ["NG"] },
  { label: "₺", goal: 100, quick: [20, 50, 100], regions: ["TR"] },
].map(c => ({ ...c, prefix: true }));

// Time zone first: it says where the phone actually is, while many phones in
// the Philippines run English (US) and would report "US" from the language.
const ZONE_REGION = {
  "Asia/Manila": "PH", "Asia/Tokyo": "JP", "Asia/Shanghai": "CN", "Asia/Seoul": "KR", "Asia/Kolkata": "IN", "Asia/Calcutta": "IN",
  "Asia/Jakarta": "ID", "Asia/Ho_Chi_Minh": "VN", "Asia/Saigon": "VN", "Asia/Bangkok": "TH", "Asia/Kuala_Lumpur": "MY",
  "Asia/Singapore": "SG", "Asia/Hong_Kong": "HK", "Asia/Taipei": "TW", "Europe/London": "GB", "Europe/Istanbul": "TR",
  "Africa/Lagos": "NG", "America/Sao_Paulo": "BR", "America/Mexico_City": "MX", "Australia/Sydney": "AU", "Pacific/Auckland": "NZ",
};

/** The phone's two-letter region ("PH"), from its time zone, else its language; "" if unknown. */
export function deviceRegion(env = {}) {
  try {
    const zone = env.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (ZONE_REGION[zone]) return ZONE_REGION[zone];
    const langs = env.languages ?? (typeof navigator !== "undefined" ? (navigator.languages || [navigator.language]) : []);
    for (const l of langs || []) {
      const m = /^[a-z]{2,3}[-_]([A-Z]{2})\b/.exec(l || "");
      if (m) return m[1];
    }
  } catch { /* fall through */ }
  return "";
}

/** The local currency's preset for the Money group ("$" when the region isn't known). */
export function localCurrency(env) {
  const r = deviceRegion(env);
  return CURRENCIES.find(c => c.regions.includes(r)) || CURRENCIES.find(c => c.label === "$");
}

/** The picker's groups, with Money resolved to the phone's own currency. */
export function customUnitGroups(env) {
  return UNIT_GROUPS.map(g => (g.money ? { title: g.title, units: [localCurrency(env)] } : g));
}

const PRESET_BY_LABEL = Object.fromEntries([...UNIT_GROUPS.flatMap(g => g.units), ...CURRENCIES].map(p => [p.label, p]));

/** The built-in custom unit a label names, or null for a typed ("Other") name. */
export function customPreset(label) {
  return PRESET_BY_LABEL[String(label || "").trim()] || null;
}
const presetOf = (h) => (unitOf(h) === "custom" ? customPreset(h?.unitLabel) : null);

export const COUNT_MAX = 10;
export const UNIT_LABEL_MAX = 12;
const BY_ID = Object.fromEntries(HABIT_UNITS.map(u => [u.id, u]));

/** The habit's unit id; anything unknown or unset is "count". */
export function unitOf(h) {
  return (h && BY_ID[h.unit]) ? h.unit : "count";
}

export function unitDef(hOrId) {
  return BY_ID[typeof hOrId === "string" ? hOrId : unitOf(hOrId)] || BY_ID.count;
}

/** True for every unit except Count: the day's log is an amount, not taps. */
export function isAmountHabit(h) {
  if (!h || h.habitType === "nottodo" || h.habitType === "limited") return false;
  return unitOf(h) !== "count";
}

/** The daily goal number (dailyCount), never below the smallest step. */
export function dailyGoalOf(h) {
  const v = Number(h?.dailyCount);
  return v > 0 ? v : 1;
}

/** Rounds an amount to what the unit stores: one decimal for custom units, whole otherwise. */
export function roundAmount(unitId, v) {
  const n = Number(v);
  if (!isFinite(n) || n <= 0) return 0;
  return unitDef(unitId).decimals ? Math.round(n * 10) / 10 : Math.round(n);
}

/** A valid daily goal for the unit: at least the smallest step, at most its max. */
export function clampGoal(unitId, v) {
  const def = unitDef(unitId);
  const max = def.id === "count" ? COUNT_MAX : def.max;
  const min = def.decimals ? 0.1 : 1;
  const n = roundAmount(unitId, v);
  return Math.min(max, Math.max(min, n || min));
}

/** The goal a habit gets when its unit is switched to `unitId` in the form. */
export function defaultGoalFor(unitId) {
  return unitDef(unitId).goal || 1;
}

/** The day's logged amount as a number (an old `true` log = the goal, i.e. done). */
export function loggedAmount(h, rawLog) {
  if (typeof rawLog === "number") return rawLog;
  return rawLog ? dailyGoalOf(h) : 0;
}

/** The unit's word for `n` of it ("glass" / "glasses"; custom uses the habit's own label). */
export function unitWord(h, n = 2) {
  const def = unitDef(h);
  if (def.id === "custom") {
    const p = presetOf(h);
    if (p) return n === 1 && p.one ? p.one : p.label;
    return String(h?.unitLabel || "").trim();
  }
  return n === 1 ? def.one : def.many;
}

const fmtNum = (n) => {
  const r = Math.round(n * 10) / 10;
  return r.toLocaleString("en-US", { maximumFractionDigits: 1 });
};

/** Minutes as "45 min", "1h", "1h 30m". */
export function formatMinutes(min) {
  const m = Math.max(0, Math.round(Number(min) || 0));
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60), r = m % 60;
  return r ? `${h}h ${r}m` : `${h}h`;
}

/** One amount with its unit: "8 glasses", "1 page", "1h 30m", "2.5 km". */
export function formatAmount(h, n) {
  if (unitOf(h) === "time") return formatMinutes(n);
  if (presetOf(h)?.prefix) return `${presetOf(h).label}${fmtNum(n)}`;
  const word = unitWord(h, n);
  return word ? `${fmtNum(n)} ${word}` : fmtNum(n);
}

/** Progress for the card / widgets: "3/8 glasses", "4,500/10,000 steps", "20 min / 1h". */
export function formatProgress(h, current, goal = dailyGoalOf(h)) {
  if (unitOf(h) === "time") return `${formatMinutes(current)} / ${formatMinutes(goal)}`;
  if (presetOf(h)?.prefix) return `${formatAmount(h, current)}/${formatAmount(h, goal)}`;
  const word = unitWord(h, goal);
  return `${fmtNum(current)}/${fmtNum(goal)}${word ? ` ${word}` : ""}`;
}

/** The daily goal as a phrase: "8 glasses", "1h", "3× a day". A unit habit's
 *  goal has no "a day" (Jade, 2026-10-10): the "Goal" label next to it says enough. */
export function formatDailyGoal(h) {
  const goal = dailyGoalOf(h);
  if (!isAmountHabit(h)) return goal === 1 ? "once a day" : `${goal}× a day`;
  return formatAmount(h, goal);
}

/** Short tag shown beside a habit's name; "" when there's nothing to say (a plain once-a-day habit). */
export function goalTag(h) {
  const goal = dailyGoalOf(h);
  if (!isAmountHabit(h)) return goal > 1 ? `${goal}x` : "";
  return formatAmount(h, goal);
}

/** The quick-add chips for the Add amount sheet. */
export function quickAmounts(h) {
  return presetOf(h)?.quick || unitDef(h).quick || [1];
}

/** A quick-add chip's label: "+1,000", "+15 min", "+₱50". */
export function quickLabel(h, n) {
  if (unitOf(h) === "time" || presetOf(h)?.prefix) return `+${formatAmount(h, n)}`;
  return `+${fmtNum(n)}`;
}

/**
 * Whether one tap on a widget's check can log this habit without opening the
 * app: Count (+1) and Glasses (+1 glass). Bigger amounts need the Add amount sheet.
 */
export function widgetTapAddsOne(h) {
  const u = unitOf(h);
  return u === "count" || u === "glasses";
}

/**
 * The value to store after adding `delta` to the day's log. Count stays capped
 * at its goal (as it always was); amounts may go past it (12,000/10,000 steps).
 * Never below 0.
 */
export function addToLog(h, rawLog, delta) {
  const cur = loggedAmount(h, rawLog);
  const next = roundAmount(unitOf(h), Math.max(0, cur + Number(delta || 0)));
  return unitOf(h) === "count" ? Math.min(dailyGoalOf(h), next) : next;
}
